/** Hooks TanStack Query pour les modules métier arbreapains :
 *  Véhicules, Tournées, Zones de nettoyage, EPI, Assignations, Driving legs.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const API_URL = import.meta.env.VITE_API_URL;

function headers(): HeadersInit {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    headers: { ...headers(), ...(init?.headers ?? {}) },
  });
  if (res.status === 204) return undefined as T;
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || `Erreur API ${res.status}`);
  }
  return res.json();
}

// --- Types partagés ---

export interface Vehicle {
  id: number;
  name: string;
  license_plate: string | null;
  is_active: boolean;
}
export interface Round {
  id: number;
  name: string;
  description: string | null;
  is_active: boolean;
  // Horaires standards + véhicule par défaut (repris à l'affectation par jour)
  start_time: string | null; // "HH:MM"
  end_time: string | null; // "HH:MM"
  vehicle_id: number | null;
}
export interface CleaningZone {
  id: number;
  name: string;
  description: string | null;
  is_active: boolean;
}
export interface Epi {
  id: number;
  name: string;
  is_active: boolean;
}

// --- Factory générique pour CRUD simple ---
function crudHooks<T extends { id: number }, C, U>(
  resource: string,
  queryKey: string,
) {
  return {
    useList: () =>
      useQuery({
        queryKey: [queryKey],
        queryFn: () => api<T[]>(`/api/${resource}`),
      }),
    useCreate: () => {
      const qc = useQueryClient();
      return useMutation({
        mutationFn: (data: C) =>
          api<T>(`/api/${resource}`, {
            method: 'POST',
            body: JSON.stringify(data),
          }),
        onSuccess: () => qc.invalidateQueries({ queryKey: [queryKey] }),
      });
    },
    useUpdate: () => {
      const qc = useQueryClient();
      return useMutation({
        mutationFn: ({ id, data }: { id: number; data: U }) =>
          api<T>(`/api/${resource}/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
          }),
        onSuccess: () => qc.invalidateQueries({ queryKey: [queryKey] }),
      });
    },
    useDelete: () => {
      const qc = useQueryClient();
      return useMutation({
        mutationFn: (id: number) =>
          api<void>(`/api/${resource}/${id}`, { method: 'DELETE' }),
        onSuccess: () => qc.invalidateQueries({ queryKey: [queryKey] }),
      });
    },
  };
}

// --- 4 ressources simples ---

export const vehicles = crudHooks<
  Vehicle,
  { name: string; license_plate?: string | null },
  { name?: string; license_plate?: string | null; is_active?: boolean }
>('vehicles', 'vehicles');

export const rounds = crudHooks<
  Round,
  {
    name: string;
    description?: string | null;
    start_time?: string | null;
    end_time?: string | null;
    vehicle_id?: number | null;
  },
  {
    name?: string;
    description?: string | null;
    start_time?: string | null;
    end_time?: string | null;
    vehicle_id?: number | null;
    is_active?: boolean;
  }
>('rounds', 'rounds');

export const cleaningZones = crudHooks<
  CleaningZone,
  { name: string; description?: string | null },
  { name?: string; description?: string | null; is_active?: boolean }
>('cleaning-zones', 'cleaning-zones');

export const epis = crudHooks<
  Epi,
  { name: string },
  { name?: string; is_active?: boolean }
>('epis', 'epis');
